import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/api';
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Button,
  Descriptions,
  Progress,
  Statistic,
  Alert,
  Tabs,
  Tag
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useCompanyStore } from '../stores/companyStore';
import { DEFAULT_EUL_TYPE, isEulType, type EulType } from '../constants/eul-types';
import {
  calculateEnvironmentAdjustedLifespan,
  calculateLifespanBySelectedEulType,
} from '../utils/deteriorationLifespan';
import {
  buildHazardRateSeries,
  ensureNonIncreasingSeries,
  sortPredictionsByDate,
} from './utils/deteriorationChartSeries';
import { resolveCurrentRemainingLife, resolveEndYearRemainingLife } from './utils/deteriorationRemainingLife';
import { hasRequiredHealthParameters } from './utils/healthParameterVisibility';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  ChartOptions,
  Filler,
  ChartData
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler
);

const { Title, Text } = Typography;

type EquipmentPrediction = {
  predictionDate: string;
  predictedRate: number | string;
  healthIndex?: number | string;  // 健全度
  remainingLife?: number | string | null;
  remainingLifeConfidence?: number | string | null;
};

type EquipmentTrend = {
  date?: string | Date | null;
  actualRate?: number | string | null;
};

type FactorImpact = 'high' | 'medium' | 'low';

type FactorInfo = {
  factor: string;
  impact: FactorImpact;
  value: number;
  description: string;
};

type EquipmentWithManufacturerLifespan = {
  manufacturerLifespan?: number | string | null;
  commissionedDate?: string | Date | null;
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
  dpEulType?: string | null;  // B10, B20, B30など
  dpEulInput?: number | string | null;
  dpEul?: number | string | null;
  dpBeta?: number | string | null;
  dpKEnv?: number | string | null;
  dpKMaint?: number | string | null;
  dpKMat?: number | string | null;
  dpKConst?: number | string | null;
};

const toNumber = (value: number | string | null | undefined, fallback = 0): number => {
  if (value === null || value === undefined) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toNullableNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPredictionYear = (value: string): number | null => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.getFullYear();
};

const resolveCommissionedLikeDate = (input: {
  commissionedDate?: string | Date | null;
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
}): Date | null => {
  const candidates = [input.commissionedDate, input.installationDate, input.startDate];
  for (const value of candidates) {
    if (!value) {
      continue;
    }
    const parsed = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }
  return null;
};

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

const YEARS_IN_MILLISECONDS = 365.25 * 24 * 60 * 60 * 1000;
const COEFFICIENT_MIN = 0.5;
const COEFFICIENT_MAX = 2.0;
const SCENARIO_BRANCH_EXPONENT = 1.6;
const SCENARIO_K_MULTIPLIER = {
  optimistic: { env: 1.05, maint: 1.1, mat: 1.03, cons: 1.02 },
  pessimistic: { env: 0.95, maint: 0.9, mat: 0.97, cons: 0.98 },
} as const;
const SCENARIO_ASSUMPTION_TEXT =
  '根拠: ワイブル式 + k係数感度分析（k_env ±5%, k_maint ±10%, k_mat ±3%, k_const ±2%、係数レンジ0.5〜2.0）';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const calculateWeibullHealth = (
  ageYears: number,
  eulYears: number,
  beta: number,
  kComposite: number,
): number => {
  if (!(eulYears > 0) || !(beta > 0) || !(kComposite > 0)) {
    return 0;
  }
  const normalizedAge = Math.max(0, ageYears);
  const eta = eulYears * kComposite;
  const deteriorationRate = 100 * (1 - Math.exp(-Math.pow(normalizedAge / eta, beta)));
  return roundToTwoDecimals(clamp(100 - deteriorationRate, 0, 100));
};

const getPredictionHealth = (prediction: EquipmentPrediction): number => {
  if (prediction.healthIndex !== undefined && prediction.healthIndex !== null) {
    return roundToTwoDecimals(clamp(toNumber(prediction.healthIndex), 0, 100));
  }
  return roundToTwoDecimals(clamp(100 - toNumber(prediction.predictedRate), 0, 100));
};

const buildScenarioSeries = (
  predictions: EquipmentPrediction[],
  baselineSeries: number[],
  equipment: EquipmentWithManufacturerLifespan | undefined,
): {
  optimisticSeries: number[];
  pessimisticSeries: number[];
  modelBased: boolean;
  assumptionText: string;
} => {
  const count = baselineSeries.length;
  if (count === 0) {
    return {
      optimisticSeries: [],
      pessimisticSeries: [],
      modelBased: false,
      assumptionText: '予測データ不足',
    };
  }

  const eul = toNullableNumber(equipment?.dpEul);
  const beta = toNullableNumber(equipment?.dpBeta);
  const baseKEnv = toNullableNumber(equipment?.dpKEnv) ?? 1;
  const baseKMaint = toNullableNumber(equipment?.dpKMaint) ?? 1;
  const baseKMat = toNullableNumber(equipment?.dpKMat) ?? 1;
  const baseKConst = toNullableNumber(equipment?.dpKConst) ?? 1;
  const commissionedDate = resolveCommissionedLikeDate({
    commissionedDate: equipment?.commissionedDate,
    installationDate: equipment?.installationDate,
    startDate: equipment?.startDate,
  });

  const isModelReady =
    eul !== null &&
    eul > 0 &&
    beta !== null &&
    beta > 0 &&
    commissionedDate !== null &&
    !Number.isNaN(commissionedDate.getTime());

  if (!isModelReady) {
    // パラメータ不足時は、従来どおり将来ほど分岐幅が増える単純シナリオでフォールバック
    const optimisticSeries = baselineSeries.map((health, index) => {
      const progress = count <= 1 ? 1 : index / (count - 1);
      const adjustment = 8 * Math.pow(progress, SCENARIO_BRANCH_EXPONENT);
      return roundToTwoDecimals(clamp(health + adjustment, 0, 100));
    });
    const pessimisticSeries = baselineSeries.map((health, index) => {
      const progress = count <= 1 ? 1 : index / (count - 1);
      const adjustment = 8 * Math.pow(progress, SCENARIO_BRANCH_EXPONENT);
      return roundToTwoDecimals(clamp(health - adjustment, 0, 100));
    });
    return {
      optimisticSeries,
      pessimisticSeries,
      modelBased: false,
      assumptionText: 'パラメータ不足のため簡易シナリオ（将来ほど分岐）',
    };
  }

  const optimisticKComposite =
    clamp(baseKEnv * SCENARIO_K_MULTIPLIER.optimistic.env, COEFFICIENT_MIN, COEFFICIENT_MAX) *
    clamp(baseKMaint * SCENARIO_K_MULTIPLIER.optimistic.maint, COEFFICIENT_MIN, COEFFICIENT_MAX) *
    clamp(baseKMat * SCENARIO_K_MULTIPLIER.optimistic.mat, COEFFICIENT_MIN, COEFFICIENT_MAX) *
    clamp(baseKConst * SCENARIO_K_MULTIPLIER.optimistic.cons, COEFFICIENT_MIN, COEFFICIENT_MAX);

  const pessimisticKComposite =
    clamp(baseKEnv * SCENARIO_K_MULTIPLIER.pessimistic.env, COEFFICIENT_MIN, COEFFICIENT_MAX) *
    clamp(baseKMaint * SCENARIO_K_MULTIPLIER.pessimistic.maint, COEFFICIENT_MIN, COEFFICIENT_MAX) *
    clamp(baseKMat * SCENARIO_K_MULTIPLIER.pessimistic.mat, COEFFICIENT_MIN, COEFFICIENT_MAX) *
    clamp(baseKConst * SCENARIO_K_MULTIPLIER.pessimistic.cons, COEFFICIENT_MIN, COEFFICIENT_MAX);

  const optimisticSeries = baselineSeries.map((baselineHealth, index) => {
    const predictionDate = new Date(predictions[index].predictionDate);
    if (Number.isNaN(predictionDate.getTime())) {
      return baselineHealth;
    }
    const ageYears = Math.max(0, (predictionDate.getTime() - commissionedDate.getTime()) / YEARS_IN_MILLISECONDS);
    const rawHealth = calculateWeibullHealth(ageYears, eul, beta, optimisticKComposite);
    const progress = count <= 1 ? 1 : index / (count - 1);
    const branchWeight = Math.pow(progress, SCENARIO_BRANCH_EXPONENT);
    const blended = baselineHealth + (rawHealth - baselineHealth) * branchWeight;
    return roundToTwoDecimals(clamp(blended, 0, 100));
  });

  const pessimisticSeries = baselineSeries.map((baselineHealth, index) => {
    const predictionDate = new Date(predictions[index].predictionDate);
    if (Number.isNaN(predictionDate.getTime())) {
      return baselineHealth;
    }
    const ageYears = Math.max(0, (predictionDate.getTime() - commissionedDate.getTime()) / YEARS_IN_MILLISECONDS);
    const rawHealth = calculateWeibullHealth(ageYears, eul, beta, pessimisticKComposite);
    const progress = count <= 1 ? 1 : index / (count - 1);
    const branchWeight = Math.pow(progress, SCENARIO_BRANCH_EXPONENT);
    const blended = baselineHealth + (rawHealth - baselineHealth) * branchWeight;
    return roundToTwoDecimals(clamp(blended, 0, 100));
  });

  return {
    optimisticSeries,
    pessimisticSeries,
    modelBased: true,
    assumptionText: SCENARIO_ASSUMPTION_TEXT,
  };
};

// dpEulType（B10, B20, B30など）から交換推奨閾値（健全度）を計算
const getReplacementThreshold = (dpEulType: string | null | undefined): number => {
  if (!dpEulType) return 70; // デフォルトB30 = 70%
  const bLifePercent = parseInt(dpEulType.replace(/^B/i, ''), 10);
  if (isNaN(bLifePercent) || bLifePercent <= 0 || bLifePercent >= 100) {
    return 70; // 不正な値の場合はデフォルト
  }
  return 100 - bLifePercent; // B30 → 70%, B20 → 80%, B10 → 90%
};

// dpEulTypeからラベル用のパーセント値を取得
const getBLifePercent = (dpEulType: string | null | undefined): number => {
  if (!dpEulType) return 30; // デフォルトB30
  const bLifePercent = parseInt(dpEulType.replace(/^B/i, ''), 10);
  return isNaN(bLifePercent) ? 30 : bLifePercent;
};

type ChartViewMode = 'health' | 'hazard';

export function DeteriorationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [chartViewMode, setChartViewMode] = useState<ChartViewMode>('health');
  const [isDetailGuidanceOpen, setIsDetailGuidanceOpen] = useState(false);

  // 健全度管理方式を取得（メーカー耐用年数Verの場合は一覧にリダイレクト）
  const { healthManagementMode, setHealthManagementMode } = useCompanyStore();
  const { data: currentHealthMode, isLoading: modeLoading } = trpc.company.getHealthManagementMode.useQuery();

  useEffect(() => {
    if (!currentHealthMode) {
      return;
    }
    setHealthManagementMode(currentHealthMode);
    if (currentHealthMode === 'manufacturer_lifespan') {
      navigate('/app/deterioration', { replace: true });
    }
  }, [currentHealthMode, navigate, setHealthManagementMode]);

  const isManufacturerLifespanMode = (currentHealthMode || healthManagementMode) === 'manufacturer_lifespan';

  // APIからデータ取得
  const { data: equipment, isLoading: equipmentLoading, error: equipmentError } = trpc.equipment.getById.useQuery(
    { id: id || '' },
    { enabled: !!id }
  );

  const { data: deteriorationTrendsResponse, isLoading: trendsLoading, error: trendsError } = trpc.deterioration.getTrends.useQuery(
    { equipmentId: id || '' },
    { enabled: !!id }
  );

  const { data: predictionsResponse, isLoading: predictionsLoading, error: predictionsError } = trpc.deterioration.predictForEquipment.useQuery(
    { equipmentId: id || '', yearsAhead: 10 },
    { enabled: !!id }
  );

  const equipmentWithLifespan = equipment as EquipmentWithManufacturerLifespan | undefined;
  const deteriorationTrends = deteriorationTrendsResponse as EquipmentTrend[] | undefined;
  const predictions = predictionsResponse as EquipmentPrediction[] | undefined;
  const sortedPredictions = sortPredictionsByDate(predictions);

  const isLoading = equipmentLoading || trendsLoading || predictionsLoading || modeLoading;
  const hasError = equipmentError || trendsError || predictionsError;
  const manufacturerLifespanValue = toNullableNumber(equipmentWithLifespan?.manufacturerLifespan);
  const equipmentStartDate = resolveCommissionedLikeDate({
    commissionedDate: equipmentWithLifespan?.commissionedDate,
    installationDate: equipmentWithLifespan?.installationDate,
    startDate: equipmentWithLifespan?.startDate,
  });
  const dpEulValue = toNullableNumber(equipment?.dpEul);
  const dpBetaValue = toNullableNumber(equipment?.dpBeta);
  const hasCommissionedLikeDate = equipmentStartDate !== null;
  const hasHealthParams = hasRequiredHealthParameters('complex', {
    dpEul: equipment?.dpEul,
    dpBeta: equipment?.dpBeta,
    manufacturerLifespan: equipmentWithLifespan?.manufacturerLifespan,
    commissionedDate: equipmentWithLifespan?.commissionedDate,
    installationDate: equipmentWithLifespan?.installationDate,
    startDate: equipmentWithLifespan?.startDate,
  });
  const missingComplexParams: string[] = [];
  if (!(dpEulValue !== null && dpEulValue > 0)) {
    missingComplexParams.push('メーカー提供の耐用年数');
  }
  if (!(dpBetaValue !== null && dpBetaValue > 0)) {
    missingComplexParams.push('設備タイプ');
  }
  if (!hasCommissionedLikeDate) {
    missingComplexParams.push('稼働開始日');
  }
  const parsedCurrentRate =
    equipment?.currentDeteriorationRate !== null && equipment?.currentDeteriorationRate !== undefined
      ? Number(equipment.currentDeteriorationRate)
      : Number.NaN;
  const hasPredictionData = hasHealthParams && sortedPredictions.length > 0;
  const currentYear = new Date().getFullYear();
  const currentPrediction = hasPredictionData
    ? sortedPredictions.find((prediction) => toPredictionYear(prediction.predictionDate) === currentYear) ?? sortedPredictions[0]
    : null;
  const predictedHealthSeries = hasPredictionData
    ? ensureNonIncreasingSeries(sortedPredictions.map(getPredictionHealth))
    : [];
  const predictedHazardRateSeries = hasPredictionData
    ? buildHazardRateSeries(predictedHealthSeries)
    : [];
  const currentPredictionIndex = currentPrediction
    ? sortedPredictions.findIndex((prediction) => prediction.predictionDate === currentPrediction.predictionDate)
    : -1;
  const predictionCurrentRate = toNullableNumber(currentPrediction?.predictedRate);
  const predictionCurrentHealth = toNullableNumber(currentPrediction?.healthIndex)
    ?? (predictionCurrentRate !== null ? 100 - predictionCurrentRate : null);
  const predictionCurrentRemainingLife = toNullableNumber(currentPrediction?.remainingLife);
  const currentHazardRate =
    currentPredictionIndex >= 0
      ? predictedHazardRateSeries[currentPredictionIndex] ?? null
      : null;
  const hasHealthValue =
    hasHealthParams &&
    (predictionCurrentHealth !== null || Number.isFinite(parsedCurrentRate));
  const currentHealthPercent = hasHealthValue
    ? roundToTwoDecimals(
      Math.max(
        0,
        Math.min(
          100,
          predictionCurrentHealth ?? (100 - parsedCurrentRate),
        ),
      ),
    )
    : null;

  const selectedEulType: EulType =
    isEulType(equipmentWithLifespan?.dpEulType) ? equipmentWithLifespan.dpEulType : DEFAULT_EUL_TYPE;
  const environmentAdjustedLifespanLabel =
    selectedEulType === 'UNKNOWN'
      ? '環境整備耐用年数(B値)'
      : `環境整備耐用年数(${selectedEulType})`;
  const manufacturerProvidedLifespanYears = toNullableNumber(equipmentWithLifespan?.dpEulInput);
  const betaForEulCalculation = toNullableNumber(equipmentWithLifespan?.dpBeta) ?? 2;
  const calculatedLifespanBySelectedTypeYears = calculateLifespanBySelectedEulType(
    dpEulValue,
    selectedEulType,
    betaForEulCalculation,
  );
  const environmentAdjustedLifespanYears = calculateEnvironmentAdjustedLifespan(
    calculatedLifespanBySelectedTypeYears ?? manufacturerProvidedLifespanYears,
    toNullableNumber(equipmentWithLifespan?.dpKEnv),
    toNullableNumber(equipmentWithLifespan?.dpKMaint),
  );
  const elapsedYearsFromCommissionedDate =
    equipmentStartDate
      ? Math.max(0, (Date.now() - equipmentStartDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
      : null;
  const remainingLifespanYearsByInput =
    environmentAdjustedLifespanYears !== null && elapsedYearsFromCommissionedDate !== null
      ? roundToTwoDecimals(environmentAdjustedLifespanYears - elapsedYearsFromCommissionedDate)
      : null;
  const {
    remainingLifespanYears,
    isModelBasedRemainingLife,
    isCappedByInputBasedRemainingLife,
  } = resolveCurrentRemainingLife(predictionCurrentRemainingLife, remainingLifespanYearsByInput);

  // メーカー耐用年数Verの場合は詳細ページを表示しない
  if (isManufacturerLifespanMode && !modeLoading) {
    return null; // リダイレクトされるので何も表示しない
  }

  // チャートデータの生成
  const generateHealthChartData = (): ChartData<'line'> => {
    if (sortedPredictions.length === 0 || !equipment) {
      return { labels: [], datasets: [] };
    }

    // メーカー耐用年数Ver用のシンプルなグラフ
    if (isManufacturerLifespanMode) {
      const manufacturerLifespan = toNumber(equipmentWithLifespan?.manufacturerLifespan, 10);
      const startDate = resolveCommissionedLikeDate({
        commissionedDate: equipmentWithLifespan?.commissionedDate,
        installationDate: equipmentWithLifespan?.installationDate,
        startDate: equipmentWithLifespan?.startDate,
      }) ?? new Date();
      const ageInYears = Math.max(0, (Date.now() - startDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));

      // 過去から未来まで表示（マイナス2年から耐用年数+5年まで）
      const labels = [];
      const healthData = [];
      const lifespanThreshold = [];

      for (let year = Math.max(0, Math.floor(ageInYears) - 2); year <= manufacturerLifespan + 5; year++) {
        labels.push(year.toString());
        const ratio = year / manufacturerLifespan;

        let healthIndex;
        if (ratio <= 0.8) {
          healthIndex = 100 * (1 - ratio);
        } else if (ratio <= 1.0) {
          const excessRatio = (ratio - 0.8) / 0.2;
          healthIndex = 20 * (1 - Math.pow(excessRatio, 1.3));
        } else {
          healthIndex = 0;
        }

        healthData.push(Math.max(0, healthIndex));
        lifespanThreshold.push(0); // 耐用年数到達点
      }

      return {
        labels,
        datasets: [
          {
            label: '健全度',
            data: healthData,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.5)',
            tension: 0.3,
          },
          {
            label: `メーカー耐用年数 (${manufacturerLifespan}年)`,
            data: lifespanThreshold,
            borderColor: 'rgba(255, 77, 79, 0.8)',
            borderWidth: 2,
            borderDash: [5, 5],
            pointRadius: 0,
            fill: false,
          },
        ],
      };
    }

    // 複雑Ver用の既存グラフ


    const labels = sortedPredictions.map((prediction) =>
      new Date(prediction.predictionDate).getFullYear().toString()
    );

    // 劣化率→健全度に変換
    const actualHealthByYear = new Map<number, number>();
    (deteriorationTrends ?? []).forEach((trend) => {
      const rate = toNullableNumber(trend.actualRate);
      const trendYear = trend.date ? new Date(trend.date).getFullYear() : null;
      if (rate === null || trendYear === null || Number.isNaN(trendYear)) {
        return;
      }
      actualHealthByYear.set(
        trendYear,
        roundToTwoDecimals(Math.max(0, Math.min(100, 100 - rate))),
      );
    });

    const currentHealthIndex =
      currentHealthPercent ?? (
        sortedPredictions.length > 0
          ? (
            toNullableNumber(sortedPredictions[0]?.healthIndex)
            ?? (toNullableNumber(sortedPredictions[0]?.predictedRate) !== null
              ? 100 - Number(sortedPredictions[0]?.predictedRate)
              : 0)
          )
          : 0
      );

    const predictedData = ensureNonIncreasingSeries(sortedPredictions.map(getPredictionHealth));
    const scenarioSeries = buildScenarioSeries(sortedPredictions, predictedData, equipmentWithLifespan);
    const optimisticData = ensureNonIncreasingSeries(scenarioSeries.optimisticSeries);
    const pessimisticData = ensureNonIncreasingSeries(scenarioSeries.pessimisticSeries);

    // 設備のdpEulTypeに基づいて動的に閾値を設定
    const dpEulType = equipmentWithLifespan?.dpEulType;
    const replacementThresholdValue = getReplacementThreshold(dpEulType);
    const bLifePercent = getBLifePercent(dpEulType);
    // 危険水準は交換推奨より20%低い値（ただし最小10%）
    const dangerThresholdValue = Math.max(10, replacementThresholdValue - 20);

    const replacementThreshold = labels.map(() => replacementThresholdValue);
    const dangerThreshold = labels.map(() => dangerThresholdValue);

    return {
      labels,
      datasets: [
        {
          label: '実測値',
          data: labels.map((year: string) => {
            const yearNum = parseInt(year, 10);
            if (yearNum === currentYear) {
              return roundToTwoDecimals(Math.max(0, Math.min(100, currentHealthIndex)));
            }
            if (yearNum < currentYear) {
              return actualHealthByYear.get(yearNum) ?? null;
            }
            return null;
          }) as Array<number | null>,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1
        },
        {
          label: '予測値 (標準)',
          data: predictedData,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          borderDash: [5, 5],
          tension: 0.1
        },
        {
          label: scenarioSeries.modelBased ? '予測値 (楽観:係数改善)' : '予測値 (楽観)',
          data: optimisticData,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderDash: [2, 2],
          tension: 0.1
        },
        {
          label: scenarioSeries.modelBased ? '予測値 (悲観:係数悪化)' : '予測値 (悲観)',
          data: pessimisticData,
          borderColor: 'rgb(255, 206, 86)',
          backgroundColor: 'rgba(255, 206, 86, 0.5)',
          borderDash: [10, 5],
          tension: 0.1
        },
        {
          label: `危険水準(${dangerThresholdValue}%)`,
          data: dangerThreshold,
          borderColor: 'rgba(255, 77, 79, 0.6)',
          backgroundColor: 'rgba(255, 77, 79, 0.3)',
          borderWidth: 2,
          pointRadius: 0,
          fill: 'origin',
          tension: 0,
          order: 10
        },
        {
          label: `交換推奨(${replacementThresholdValue}%) - B${bLifePercent}`,
          data: replacementThreshold,
          borderColor: 'rgba(250, 173, 20, 0.6)',
          backgroundColor: 'rgba(250, 173, 20, 0.2)',
          borderWidth: 2,
          pointRadius: 0,
          fill: '-1',
          tension: 0,
          order: 9
        }
      ]
    };
  };

  const healthChartData = generateHealthChartData();

  const generateHazardChartData = (): ChartData<'line'> => {
    if (healthChartData.labels.length === 0 || healthChartData.datasets.length === 0) {
      return { labels: [], datasets: [] };
    }

    const hazardDatasets = healthChartData.datasets
      .filter((dataset) => {
        const label = dataset.label ?? '';
        return label.includes('予測値');
      })
      .map((dataset) => {
        const label = dataset.label ?? '';
        const hazardLabel =
          label.includes('標準')
            ? '標準（今後1年の故障リスク）'
            : label.includes('楽観')
              ? '楽観（今後1年の故障リスク）'
              : label.includes('悲観')
                ? '悲観（今後1年の故障リスク）'
                : '今後1年の故障リスク';

        return {
          ...dataset,
          label: hazardLabel,
          data: buildHazardRateSeries((dataset.data ?? []) as Array<number | null | undefined>),
          fill: false,
        };
      });

    return {
      labels: [...healthChartData.labels],
      datasets: hazardDatasets,
    };
  };

  const hazardChartData = generateHazardChartData();
  const activeChartData = chartViewMode === 'hazard' ? hazardChartData : healthChartData;

  // グラフの最大値を計算（閾値ラインを除く実データのみ）
  const calculateMaxRate = (chartData: ChartData<'line'>, viewMode: ChartViewMode) => {
    const allRates: number[] = [];
    chartData.datasets.forEach((dataset) => {
      const label = dataset.label || '';
      if (viewMode === 'hazard' || (!label.includes('交換推奨') && !label.includes('危険水準'))) {
        const datasetValues = (dataset.data ?? []) as Array<number | null>;
        datasetValues.forEach((value) => {
          if (typeof value === 'number' && value > 0) {
            allRates.push(value);
          }
        });
      }
    });
    return allRates.length > 0 ? Math.max(...allRates) : 0;
  };

  const maxRate = calculateMaxRate(activeChartData, chartViewMode);
  const yAxisMin = chartViewMode === 'hazard' ? 0 : maxRate >= 40 ? 40 : 0;
  const yAxisMax =
    chartViewMode === 'hazard'
      ? Math.min(100, Math.max(20, Math.ceil((maxRate + 5) / 5) * 5))
      : 100;

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text:
          chartViewMode === 'hazard'
            ? 'ハザード率（今後1年の故障リスク）'
            : isManufacturerLifespanMode
              ? '健全度推移（経年劣化）'
              : '健全度予測グラフ'
      }
    },
    scales: {
      y: {
        min: yAxisMin,
        max: yAxisMax,
        title: {
          display: true,
          text: chartViewMode === 'hazard' ? '今後1年の故障リスク (%)' : '健全度 (%)'
        }
      },
      x: {
        title: {
          display: true,
          text: isManufacturerLifespanMode ? '経過年数（年）' : '年度'
        }
      }
    }
  };

  const chartDescription =
    chartViewMode === 'hazard'
      ? {
        message: 'ハザード率（今後1年の故障リスク）の見方',
        type: 'warning' as const,
        lines: [
          'ハザード率は、今この年まで動いている前提で、次の1年に故障する確率の目安です。',
          '健全度が同じでも、ハザード率が上がるほど故障しやすさの上がり方が急になっています。',
          '健全度: 同じ条件で使う設備群のうち、まだ故障していない割合を見る指標です。',
          '残存耐用年数: 交換検討ラインまでの目安をつかむ指標です。',
          'ハザード率: 次の期間で故障リスクがどれくらい高まるかを見る指標です。',
        ],
      }
      : {
        message: '健全度グラフの見方',
        type: 'info' as const,
        lines: [
          '健全度グラフは、同じ条件で使う設備群のうち、まだ故障していない割合の推移を見るためのものです。',
          '残存耐用年数は、交換検討ラインまでの目安として別に読み取ってください。',
        ],
      };

  // 影響要因データの生成
  const generateFactorData = (): FactorInfo[] => {
    if (!equipment) return [];

    const factors: FactorInfo[] = [];

    // 環境係数
    if (equipment.dpKEnv) {
      const kEnv = parseFloat(equipment.dpKEnv);
      factors.push({
        factor: '環境条件',
        impact: kEnv > 1.5 ? 'high' : kEnv > 1.2 ? 'medium' : 'low',
        value: kEnv,
        description: kEnv > 1.5 ? '厳しい環境条件' : kEnv > 1.2 ? '標準的な環境' : '良好な環境'
      });
    }

    // メンテナンス係数
    if (equipment.dpKMaint) {
      const kMaint = parseFloat(equipment.dpKMaint);
      factors.push({
        factor: 'メンテナンス状態',
        impact: kMaint > 1.5 ? 'high' : kMaint > 1.2 ? 'medium' : 'low',
        value: kMaint,
        description: kMaint > 1.5 ? 'メンテナンス不足' : kMaint > 1.2 ? '標準的なメンテナンス' : '十分なメンテナンス'
      });
    }

    // 材質係数
    if (equipment.dpKMat) {
      const kMat = parseFloat(equipment.dpKMat);
      factors.push({
        factor: '材質品質',
        impact: kMat > 1.5 ? 'high' : kMat > 1.2 ? 'medium' : 'low',
        value: kMat,
        description: kMat > 1.5 ? '材質品質が低下' : kMat > 1.2 ? '標準的な材質' : '高品質材質'
      });
    }

    // 経年変化の計算
    if (equipmentStartDate) {
      const ageInYears = (Date.now() - equipmentStartDate.getTime()) / (365 * 24 * 60 * 60 * 1000);
      const ageFactor = 1 + (ageInYears / 10) * 0.5; // 10年で係数0.5上昇
      factors.push({
        factor: '経年変化',
        impact: ageFactor > 1.5 ? 'high' : ageFactor > 1.2 ? 'medium' : 'low',
        value: ageFactor,
        description: `設置後${Math.round(ageInYears)}年経過`
      });
    }

    return factors;
  };

  const factorData = generateFactorData();

  const scenarioSummary = (() => {
    if (!hasPredictionData || sortedPredictions.length === 0) {
      return null;
    }

    const baselineSeries = ensureNonIncreasingSeries(sortedPredictions.map(getPredictionHealth));
    const scenarioSeries = buildScenarioSeries(sortedPredictions, baselineSeries, equipmentWithLifespan);
    const lastIndex = sortedPredictions.length - 1;
    const lastPrediction = sortedPredictions[lastIndex];
    const endYear = new Date(lastPrediction.predictionDate).getFullYear();
    const usePredictionRemainingLife = predictionCurrentRemainingLife !== null;
    const endYearRemainingLifeFromPrediction = usePredictionRemainingLife
      ? toNullableNumber(lastPrediction.remainingLife)
      : null;
    const currentPredictionYear = currentPrediction
      ? new Date(currentPrediction.predictionDate).getFullYear()
      : currentYear;

    return {
      endYear,
      standardHealthAtEnd: baselineSeries[lastIndex],
      optimisticHealthAtEnd: scenarioSeries.optimisticSeries[lastIndex] ?? baselineSeries[lastIndex],
      pessimisticHealthAtEnd: scenarioSeries.pessimisticSeries[lastIndex] ?? baselineSeries[lastIndex],
      endYearRemainingLife: resolveEndYearRemainingLife({
        endYearRemainingLifeFromPrediction,
        currentPredictionYear,
        endYear,
        currentRemainingLife: remainingLifespanYears,
      }),
      currentRemainingLife: remainingLifespanYears,
      modelBased: scenarioSeries.modelBased,
      assumptionText: scenarioSeries.assumptionText,
    };
  })();

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  // ローディング状態の表示
  if (isLoading) {
    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/deterioration')}
          >
            全体に戻る
          </Button>
          <Button
            onClick={() => navigate(`/app/equipments/${id}`)}
          >
            設備詳細
          </Button>
        </Space>
        <Card loading={true}>
          <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            データを読み込み中...
          </div>
        </Card>
      </div>
    );
  }

  // エラー状態の表示
  if (hasError || !equipment) {
    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/deterioration')}
          >
            全体に戻る
          </Button>
          <Button
            onClick={() => navigate(`/app/equipments/${id}`)}
          >
            設備詳細
          </Button>
        </Space>
        <Alert
          message="エラー"
          description={
            equipmentError?.message ||
            trendsError?.message ||
            predictionsError?.message ||
            "設備データの取得に失敗しました"
          }
          type="error"
          action={
            <Button onClick={() => window.location.reload()}>
              再読み込み
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/app/deterioration')}
        >
          全体に戻る
        </Button>
        <Button
          onClick={() => navigate(`/app/equipments/${id}`)}
        >
          設備詳細
        </Button>
      </Space>

      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={2}>健全度予測詳細</Title>
        <Tag color={isManufacturerLifespanMode ? 'blue' : 'purple'}>
          {isManufacturerLifespanMode ? '耐用年数モード' : '健全度モード'}
        </Tag>
      </Space>

      {/* パラメータ不足時のアラート */}
      {(() => {
        if (missingComplexParams.length > 0) {
          return (
            <Alert
              message="健全度予測に必要なパラメータが不足しています"
              description={
                <Space direction="vertical" size="small">
                  <Text>以下のパラメータを設定してください：</Text>
                  <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                    {missingComplexParams.map((param) => (
                      <li key={param}>{param}</li>
                    ))}
                  </ul>
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => navigate(`/app/equipments/${id}`)}
                  >
                    設備を編集する
                  </Button>
                </Space>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          );
        }
        return null;
      })()}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="設備情報">
            <Descriptions column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="設備名">{equipment.name}</Descriptions.Item>
              <Descriptions.Item label="カテゴリー">
                {(() => {
                  const categoryMap: { [key: string]: string } = {
                    'pump': 'ポンプ',
                    'motor': 'モーター',
                    'compressor': 'コンプレッサー',
                    'tank': 'タンク',
                    'valve': 'バルブ',
                    'other': 'その他'
                  };
                  const equipmentRecord = equipment as Record<string, unknown>;
                  const categoryInfo = equipmentRecord.categoryInfo as
                    | { categoryLabel?: string | null; categoryKey?: string | null }
                    | null
                    | undefined;

                  if (categoryInfo) {
                    if (categoryInfo.categoryLabel) {
                      return categoryInfo.categoryLabel;
                    }
                    if (categoryInfo.categoryKey) {
                      return categoryMap[categoryInfo.categoryKey] ?? categoryInfo.categoryKey;
                    }
                  }

                  if (typeof equipment.category === 'string' && equipment.category) {
                    return categoryMap[equipment.category] ?? equipment.category;
                  }

                  if (equipment.category && typeof equipment.category === 'object') {
                    const { categoryLabel, categoryKey } = equipment.category as {
                      categoryLabel?: string | null;
                      categoryKey?: string | null;
                    };
                    if (categoryLabel) {
                      return categoryLabel;
                    }
                    if (categoryKey) {
                      return categoryMap[categoryKey] ?? categoryKey;
                    }
                  }

                  const fallbackCategoryKey = (equipmentRecord.categoryKey as string | null | undefined)?.trim();
                  if (fallbackCategoryKey) {
                    return categoryMap[fallbackCategoryKey] ?? fallbackCategoryKey;
                  }

                  const fallbackCategoryLabel = (equipmentRecord.categoryLabel as string | null | undefined)?.trim();
                  if (fallbackCategoryLabel) {
                    return fallbackCategoryLabel;
                  }

                  return '-';
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="メーカー">{equipment.manufacturerSupplier?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="型番">{equipment.model || '-'}</Descriptions.Item>
              <Descriptions.Item label="稼働開始日">
                {equipmentStartDate ? equipmentStartDate.toLocaleDateString('ja-JP') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="健全度予測グラフ" style={{ marginTop: 16 }}>
            {!isManufacturerLifespanMode && (
              <Space style={{ marginBottom: 16 }} wrap>
                <Button
                  type={chartViewMode === 'health' ? 'primary' : 'default'}
                  onClick={() => setChartViewMode('health')}
                  data-testid="deterioration-chart-view-health"
                >
                  健全度
                </Button>
                <Button
                  type={chartViewMode === 'hazard' ? 'primary' : 'default'}
                  onClick={() => setChartViewMode('hazard')}
                  data-testid="deterioration-chart-view-hazard"
                >
                  ハザード率（今後1年の故障リスク）
                </Button>
              </Space>
            )}
            <Line options={chartOptions} data={activeChartData} />
            {!isManufacturerLifespanMode && (
              <Alert
                message={chartDescription.message}
                description={(
                  <Space direction="vertical" size={4}>
                    {chartDescription.lines.map((line) => (
                      <Text key={line}>{line}</Text>
                    ))}
                  </Space>
                )}
                type={chartDescription.type}
                showIcon
                style={{ marginTop: 16 }}
                data-testid="deterioration-chart-view-description"
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="現在の状態">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {!isManufacturerLifespanMode && (
                <div>
                  <Button
                    type="link"
                    onClick={() => setIsDetailGuidanceOpen((open) => !open)}
                    icon={isDetailGuidanceOpen ? <DownOutlined /> : <RightOutlined />}
                    aria-label="この設備詳細の見方"
                    aria-expanded={isDetailGuidanceOpen}
                    data-testid="deterioration-detail-guidance-toggle"
                    style={{ padding: 0, height: 'auto', fontWeight: 600 }}
                  >
                    この設備詳細の見方
                  </Button>
                  {isDetailGuidanceOpen && (
                    <Alert
                      description={
                        <Space direction="vertical" size={4}>
                          <Text>
                            健全度は、この設備が属する同条件グループで見た「未故障率」の目安です。1台の今すぐ故障確率そのものではありません。
                          </Text>
                          <Text>
                            残存耐用年数0年は、交換検討ラインに達した目安です。更新判断は重要度や停止影響もあわせて確認してください。
                          </Text>
                          <Text>
                            この詳細画面では、下のグラフで健全度の推移と、ハザード率ビューで故障しやすさの上がり方を確認できます。
                          </Text>
                        </Space>
                      }
                      type="info"
                      showIcon
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              )}
              <div>
                <Text type="secondary">現在の健全度</Text>
                {currentHealthPercent !== null ? (
                  <Progress
                    percent={currentHealthPercent}
                    format={(percent) => `${(percent ?? 0).toFixed(2)}%`}
                    strokeColor={{
                      '0%': '#ff4d4f',
                      '50%': '#faad14',
                      '100%': '#52c41a'
                    }}
                  />
                ) : (
                  <div style={{ color: '#999' }}>
                    <div>算出条件不足</div>
                    {missingComplexParams.length > 0 && (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        不足項目: {missingComplexParams.join('、')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!isManufacturerLifespanMode && (
                <div data-testid="deterioration-current-hazard-rate">
                  <Statistic
                    title="現在のハザード率"
                    value={currentHazardRate ?? '-'}
                    precision={currentHazardRate !== null ? 2 : undefined}
                    suffix={currentHazardRate !== null ? '%' : ''}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    今後1年の故障リスクの目安です。
                  </Text>
                </div>
              )}

              {isManufacturerLifespanMode && (
                <>
                  <Statistic
                    title="メーカー耐用年数"
                    value={manufacturerLifespanValue ?? '-'}
                    suffix="年"
                    prefix={<CalendarOutlined />}
                  />
                  {manufacturerLifespanValue !== null && equipmentStartDate && (
                    <>
                      <Statistic
                        title="経過年数"
                        value={Math.max(0, (Date.now() - equipmentStartDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)).toFixed(1)}
                        suffix="年"
                      />
                      {(() => {
                        const lifespan = manufacturerLifespanValue ?? 0;
                        const age = Math.max(0, (Date.now() - equipmentStartDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
                        const isExpired = age >= lifespan;
                        return isExpired ? (
                          <Alert
                            message="耐用年数超過"
                            description="メーカー耐用年数を超過しています。交換を検討してください。"
                            type="error"
                            showIcon
                          />
                        ) : null;
                      })()}
                    </>
                  )}
                </>
              )}

              {!isManufacturerLifespanMode && (
                <Statistic
                  title="メーカー提供耐用年数"
                  value={
                    manufacturerProvidedLifespanYears !== null
                      ? manufacturerProvidedLifespanYears.toFixed(2)
                      : '-'
                  }
                  suffix={manufacturerProvidedLifespanYears !== null ? '年' : ''}
                />
              )}
              {!isManufacturerLifespanMode && (
                <Statistic
                  title={environmentAdjustedLifespanLabel}
                  value={
                    environmentAdjustedLifespanYears !== null
                      ? environmentAdjustedLifespanYears.toFixed(2)
                      : '-'
                  }
                  suffix={environmentAdjustedLifespanYears !== null ? '年' : ''}
                />
              )}
              {!isManufacturerLifespanMode && (
                <Statistic
                  title="残存耐用年数"
                  value={
                    remainingLifespanYears !== null
                      ? Math.max(remainingLifespanYears, 0).toFixed(2)
                      : '-'
                  }
                  suffix={remainingLifespanYears !== null ? '年' : ''}
                />
              )}
              {!isManufacturerLifespanMode && remainingLifespanYears !== null && remainingLifespanYears < 0 && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  耐用年数を {Math.abs(remainingLifespanYears).toFixed(2)} 年超過しています。
                </Text>
              )}
              {!isManufacturerLifespanMode && elapsedYearsFromCommissionedDate === null && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  稼働開始日が未設定のため、残存耐用年数は計算できません。
                </Text>
              )}
              {!isManufacturerLifespanMode && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  B値: {selectedEulType} / β={betaForEulCalculation.toFixed(2)}
                </Text>
              )}
              {!isManufacturerLifespanMode && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {`${environmentAdjustedLifespanLabel}は、B値換算寿命に設置環境係数(kEnv)・メンテナンス係数(kMaint)を反映しています。`}
                </Text>
              )}
              {!isManufacturerLifespanMode && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isModelBasedRemainingLife
                    ? '残存耐用年数は予測モデル（k係数反映）で算出したB値閾値到達までの推定値を表示しています。'
                    : `残存耐用年数は${environmentAdjustedLifespanLabel}から算出したB値閾値到達までの推定値を表示しています。`}
                </Text>
              )}
              {!isManufacturerLifespanMode && isCappedByInputBasedRemainingLife && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {`予測モデル値が${environmentAdjustedLifespanLabel}を上回るため、表示上は${environmentAdjustedLifespanLabel}を上限にしています。`}
                </Text>
              )}

            </Space>
          </Card>

          {/* 複雑Verのみ: 健全度影響要因分析を表示 */}
          {!isManufacturerLifespanMode && (
            <Card title="健全度影響要因分析" style={{ marginTop: 16 }}>
              {factorData.length > 0 ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {factorData.map((factor, index) => (
                    <div key={index}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text>{factor.factor}</Text>
                        <Text strong style={{ color: getImpactColor(factor.impact) }}>
                          係数: {typeof factor.value === 'number' ? factor.value.toFixed(2) : factor.value}
                        </Text>
                      </div>
                      <Progress
                        percent={factor.value * 30}
                        strokeColor={getImpactColor(factor.impact)}
                        showInfo={false}
                        size="small"
                      />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {factor.description}
                      </Text>
                    </div>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">
                  影響要因データが設定されていません。設備編集画面から環境係数、メンテナンス係数、材質係数を設定してください。
                </Text>
              )}
            </Card>
          )}

            <Card title="予測シナリオ" style={{ marginTop: 16 }}>
              {scenarioSummary && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
                  {scenarioSummary.assumptionText}
                </Text>
              )}
              <Tabs
                items={[
                  ...(() => {
                  if (!hasPredictionData) {
                    return [{
                      key: 'no-data',
                      label: 'データなし',
                      children: (
                        <Text type="secondary">予測データがありません</Text>
                      )
                    }];
                  }

                  if (!scenarioSummary) {
                    return [{
                      key: 'no-summary',
                      label: 'データなし',
                      children: (
                        <Text type="secondary">シナリオ要約の作成に失敗しました</Text>
                      )
                    }];
                  }

                  const hasEndYearRemainingLife = scenarioSummary.endYearRemainingLife !== null;
                  const hasCurrentRemainingLife = scenarioSummary.currentRemainingLife !== null;

                  return [
                    {
                      key: 'standard',
                      label: '標準',
                      children: (
                        <Space direction="vertical">
                          <Text>現在のメンテナンス継続</Text>
                          <Text type="secondary">
                            {scenarioSummary.endYear}年に健全度{scenarioSummary.standardHealthAtEnd.toFixed(1)}%到達
                          </Text>
                          {hasCurrentRemainingLife && (
                            <Text type="secondary">
                              現在残存耐用年数：{Math.max(scenarioSummary.currentRemainingLife, 0).toFixed(2)}年
                            </Text>
                          )}
                          {hasEndYearRemainingLife && (
                            <Text type="secondary">
                              {scenarioSummary.endYear}年時点の推定残存耐用年数：{Math.max(scenarioSummary.endYearRemainingLife, 0).toFixed(2)}年
                            </Text>
                          )}
                        </Space>
                      )
                    },
                    {
                      key: 'optimistic',
                      label: '楽観',
                      children: (
                        <Space direction="vertical">
                          <Text>{scenarioSummary.modelBased ? '係数改善を想定（感度分析）' : '予防保全強化'}</Text>
                          <Text type="secondary">
                            {scenarioSummary.endYear}年に健全度{scenarioSummary.optimisticHealthAtEnd.toFixed(1)}%
                          </Text>
                          {hasCurrentRemainingLife && (
                            <Text type="secondary">
                              現在残存耐用年数の改善余地：+{(Math.max(scenarioSummary.currentRemainingLife, 0) * 0.2).toFixed(2)}年
                            </Text>
                          )}
                        </Space>
                      )
                    },
                    {
                      key: 'pessimistic',
                      label: '悲観',
                      children: (
                        <Space direction="vertical">
                          <Text>{scenarioSummary.modelBased ? '係数悪化を想定（感度分析）' : '負荷増大想定'}</Text>
                          <Text type="secondary">
                            {scenarioSummary.endYear}年に健全度{scenarioSummary.pessimisticHealthAtEnd.toFixed(1)}%
                          </Text>
                          <Text type="warning">
                            {scenarioSummary.pessimisticHealthAtEnd < 30
                              ? '即座交換検討必要'
                              : hasCurrentRemainingLife && scenarioSummary.currentRemainingLife < 3
                                ? '早期交換検討必要'
                                : '定期点検推奨'}
                          </Text>
                        </Space>
                      )
                    }
                  ];
                })()
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

